import { useOrder, nextBulkTier, bulkDiscountFor } from "../../context/OrderContext";
import { Minus, Plus, Sparkles, Home, Lightbulb } from "lucide-react";
import MeasureLaterNote from "../MeasureLaterNote";

export default function StepQuantity() {
  const { state, update, goNext, goBack } = useOrder();
  const qty = state.quantity || 1;
  const isWindow = state.product?.type === "window";

  const setQty = (n) => update({ quantity: Math.max(1, Math.min(50, n)) });

  // Bulk-discount nudges (windows only).
  const currentDiscount = bulkDiscountFor(state.product?.type, qty);
  const next = nextBulkTier(qty);

  return (
    <div data-testid="step-quantity">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2 text-[var(--brand-text)]">
        How many?
      </h1>
      <p className="muted-text mb-5">
        Tell us how many <strong>{state.product?.name?.toLowerCase()}</strong> you need.
      </p>
      <MeasureLaterNote />

      <div className="option-card flex items-center justify-between">
        <button data-testid="qty-decrement" type="button" onClick={() => setQty(qty - 1)}
          className="w-12 h-12 rounded-full border-2 border-[var(--brand-border)] flex items-center justify-center hover:border-[var(--brand-green)]"
          aria-label="Decrease quantity">
          <Minus className="w-5 h-5" />
        </button>
        <div className="text-center">
          <p className="text-5xl font-black text-[var(--brand-text)]" data-testid="qty-value">{qty}</p>
          <p className="text-xs uppercase tracking-wider muted-text mt-1">{qty === 1 ? "item" : "items"}</p>
        </div>
        <button data-testid="qty-increment" type="button" onClick={() => setQty(qty + 1)}
          className="w-12 h-12 rounded-full border-2 border-[var(--brand-border)] flex items-center justify-center hover:border-[var(--brand-green)]"
          aria-label="Increase quantity">
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Bulk-discount nudges (windows only) */}
      {isWindow && (
        <div className="mt-3 space-y-2">
          {currentDiscount > 0 && (
            <div data-testid="bulk-discount-active"
              className="rounded-2xl px-4 py-3 border-2 flex items-center gap-2"
              style={{ background: "#dcfce7", borderColor: "#86efac" }}>
              <Sparkles className="w-4 h-4 text-green-700 shrink-0" />
              <p className="text-sm font-semibold text-green-900">
                You&apos;re saving ${currentDiscount} with bulk pricing.
              </p>
            </div>
          )}
          {next && (
            <div data-testid="bulk-nudge"
              className="rounded-2xl px-4 py-3 border-2"
              style={{ background: "#fffbeb", borderColor: "#fde68a" }}>
              <p className="text-sm font-semibold text-amber-900">
                {next.screensAway === 1
                  ? `You're 1 screen away from saving $${next.dollarOff}`
                  : `Add ${next.screensAway} more screens and save $${next.dollarOff}`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Pricing-clarity helper + behavioral nudges (windows only) */}
      {isWindow && (
        <div className="mt-3 space-y-1.5 px-1">
          <p className="text-[11px] muted-text" data-testid="pricing-clarity">
            Standard pricing applies to most windows (up to 36&quot; × 72&quot;).
          </p>
          <p className="text-[11px] muted-text flex items-start gap-1.5" data-testid="social-proof">
            <Home className="w-3 h-3 mt-0.5 shrink-0" />
            Most homes have 6&ndash;10 window screens.
          </p>
          <p className="text-[11px] muted-text flex items-start gap-1.5" data-testid="rooms-reminder">
            <Lightbulb className="w-3 h-3 mt-0.5 shrink-0" />
            Don&apos;t forget bathrooms, garage, and side windows.
          </p>
        </div>
      )}

      <div className="flex gap-3 mt-8">
        <button data-testid="back-btn" type="button" onClick={goBack}
          className="flex-1 py-3 rounded-full border-2 border-[var(--brand-border)] font-semibold">
          Back
        </button>
        <button data-testid="continue-btn" className="cta-btn flex-1" onClick={goNext}>Continue</button>
      </div>
    </div>
  );
}
